import { useState, useEffect, type ReactNode } from 'react';

import bmc from '../assets/icons/bmc.svg';
import cloudAws from '../assets/icons/cloud-aws.svg';
import cloudAzure from '../assets/icons/cloud-azure.svg';
import cloudGoogle from '../assets/icons/cloud-google.svg';
import cloudSnowflake from '../assets/icons/cloud-snowflake.svg';
import databricks from '../assets/icons/databricks.svg';
import pluralsight from '../assets/icons/pluralsight.svg';
import { Reveal } from '../components/effects/Reveal';
import {
  subscribeToCertifications,
  normalizeCertificationsSnapshot,
} from '../firebase/certificationsService';

/** Logo de marca con URL — preserva colores oficiales del SVG descargado. */
function LogoImg({ src, alt }: { src: string; alt: string }) {
  return <img src={src} alt={alt} className="w-9 h-9 object-contain" draggable={false} />;
}

interface PartnerLogo {
  name: string;
  role: string;
  icon: ReactNode;
}

const LOGOS: PartnerLogo[] = [
  {
    name: 'Google Cloud',
    role: 'Premier Partner',
    icon: <LogoImg src={cloudGoogle} alt="Google Cloud" />,
  },
  {
    name: 'AWS',
    role: 'Partner',
    icon: <LogoImg src={cloudAws} alt="Amazon Web Services" />,
  },
  {
    name: 'Microsoft Azure',
    role: 'Partner',
    icon: <LogoImg src={cloudAzure} alt="Microsoft Azure" />,
  },
  {
    name: 'Snowflake',
    role: 'Partner',
    icon: <LogoImg src={cloudSnowflake} alt="Snowflake" />,
  },
  {
    name: 'Databricks',
    role: 'Partner',
    icon: <LogoImg src={databricks} alt="Databricks" />,
  },
  {
    name: 'BMC',
    role: 'Partner',
    icon: <LogoImg src={bmc} alt="BMC Software" />,
  },
  {
    name: 'Pluralsight',
    role: 'Training',
    icon: <LogoImg src={pluralsight} alt="Pluralsight" />,
  },
];

const DEFAULT_CERTS = normalizeCertificationsSnapshot(null);

const TRAINING_STATS = [
  {
    num: '1:1',
    label: 'Plan personalizado',
    sub: 'Programas de certificación especializados alineados con su plan de carrera profesional.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="8" r="3" />
        <path d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6" />
        <path d="M16 3l2 2 4-4" />
      </svg>
    ),
  },
  {
    num: '100%',
    label: 'Compromiso eTribe',
    sub: 'Nuestro compromiso es formar líderes en las tecnologías líderes del mercado.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z" />
      </svg>
    ),
  },
  {
    num: '+40',
    label: 'Cursos vigentes',
    sub: 'Catálogo curado en Google Cloud Skills Boost, AWS Skill Builder y Pluralsight.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14l-4-2-4 2-4-2-4 2z" />
        <path d="M8 7h8M8 11h6" />
      </svg>
    ),
  },
  {
    num: '100%',
    label: 'Equipo en programa',
    sub: 'Desde junior hasta arquitecto senior. Sin excepciones.',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="9" cy="8" r="3" />
        <path d="M3 21c0-3.3 2.7-6 6-6s6 2.7 6 6" />
        <circle cx="17" cy="6" r="2.5" />
        <path d="M15 12c2.8 0 5 2.2 5 5" />
      </svg>
    ),
  },
];

export default function Partners() {
  const [certsData, setCertsData] = useState(DEFAULT_CERTS);
  const [tab, setTab] = useState<string>('gcp');
  const allLogos = [...LOGOS, ...LOGOS]; // duplicado para marquee infinito

  useEffect(() => {
    const unsubscribe = subscribeToCertifications((data) => {
      if (data) {
        setCertsData(data);
        const keys = Object.keys(data);
        if (keys.length > 0) {
          const sortedKeys = keys.sort((a, b) => data[a].order - data[b].order);
          setTab((current) => (keys.includes(current) ? current : sortedKeys[0]));
        }
      }
    });
    return unsubscribe;
  }, []);

  const providerKeys = Object.keys(certsData).sort(
    (a, b) => certsData[a].order - certsData[b].order
  );
  const currentProvider = certsData[tab] || certsData[providerKeys[0]];

  return (
    <section id="partners" className="bg-etribe-gray-bg py-[120px]">
      <div className="container-etribe">
        <div className="max-w-[720px] mb-16">
          <p className="eyebrow"><span>Ecosistema</span></p>
          <h2 className="text-[clamp(32px,3.6vw,48px)] mb-4">Respaldados por los líderes</h2>
          <p className="text-[clamp(17px,1.3vw,20px)] text-etribe-ink-2 leading-[1.55]">
            Alianzas formales con los principales proveedores cloud, datos e IA del mercado.
          </p>
        </div>

        <div
          className="overflow-hidden relative mb-20 py-8 border-y border-etribe-line"
          style={{
            maskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)',
          }}
        >
          <div className="flex items-center gap-10 sm:gap-20 w-max animate-marquee">
            {allLogos.map((l, i) => (
              <div
                key={i}
                className="group flex flex-col items-center justify-center gap-2.5 text-center min-w-[160px] flex-shrink-0 px-2 hover:-translate-y-1 transition-all duration-300"
              >
                <span
                  aria-hidden
                  className="grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-300"
                >
                  {l.icon}
                </span>
                <span className="font-heading font-bold tracking-[0.04em] text-base text-etribe-gray-500 group-hover:text-etribe-blue transition-colors">
                  {l.name}
                </span>
                <small className="font-body text-[10px] tracking-[0.14em] uppercase text-etribe-gray-2 font-medium">
                  {l.role}
                </small>
              </div>
            ))}
          </div>
        </div>

        {/* Certificaciones */}
        <Reveal>
          <div className="relative bg-white rounded-[14px] p-10 lg:p-16 border border-etribe-line grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-16 items-center overflow-hidden spotlight" style={{ ['--spot-color' as string]: 'rgba(16,3,163,0.07)', ['--spot-size' as string]: '520px' }}>
            {/* Glow azul decorativo top-left */}
            <div
              aria-hidden
              className="absolute -top-40 -left-40 w-[440px] h-[440px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(16,3,163,.08), transparent 65%)' }}
            />
            {/* Glow amarillo decorativo bottom-right */}
            <div
              aria-hidden
              className="absolute -bottom-32 -right-32 w-[380px] h-[380px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(255,200,54,.12), transparent 65%)' }}
            />
            {/* Patrón de grilla sutil */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-[0.35] pointer-events-none"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(16,3,163,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(16,3,163,.04) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
                maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
                WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
              }}
            />

            <div className="relative">
              <p className="inline-flex items-center gap-2 text-[12px] font-semibold tracking-[0.18em] uppercase text-etribe-blue mb-5">
                <span className="relative inline-flex w-2 h-2">
                  <span className="absolute inset-0 rounded-full bg-etribe-blue/40 animate-pulse-ring" />
                  <span className="relative w-2 h-2 rounded-full bg-etribe-blue" />
                </span>
                Equipo certificado
              </p>
              <div className="flex flex-col gap-3 mb-6">
                {providerKeys.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    data-cursor="grow"
                    className={[
                      'group relative flex justify-between items-center px-5 py-4 rounded-[12px] border text-left transition-all duration-300 overflow-hidden',
                      tab === key
                        ? 'bg-etribe-blue border-etribe-blue translate-x-0.5 shadow-[0_10px_30px_-12px_rgba(16,3,163,.55)]'
                        : 'bg-etribe-gray-bg border-transparent hover:bg-[#EFF1F8] hover:translate-x-0.5 hover:border-etribe-blue/15',
                    ].join(' ')}
                  >
                    {/* Barra lateral amarilla en tab activo */}
                    <span
                      aria-hidden
                      className={[
                        'absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full bg-etribe-yellow transition-all duration-300',
                        tab === key ? 'h-8 opacity-100' : 'h-0 opacity-0',
                      ].join(' ')}
                    />
                    {/* Shimmer sutil al hover en tabs inactivos */}
                    {tab !== key && (
                      <span
                        aria-hidden
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                        style={{
                          background: 'linear-gradient(110deg, transparent 30%, rgba(16,3,163,.06) 50%, transparent 70%)',
                        }}
                      />
                    )}
                    <span className={['relative font-heading text-[18px] tracking-[0.04em] uppercase transition-colors', tab === key ? 'text-white' : 'text-etribe-ink group-hover:text-etribe-blue'].join(' ')}>
                      {certsData[key].label}
                    </span>
                    <span className={['relative font-heading text-[28px] leading-none inline-flex items-baseline transition-all duration-300', tab === key ? 'text-etribe-yellow scale-110' : 'text-etribe-blue group-hover:scale-105'].join(' ')}>
                      <span className="text-[.7em] mr-px">+</span>{certsData[key].total}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-etribe-gray-500 tracking-[0.04em] border-t border-etribe-line pt-4">
                Total combinado: <span className="text-shimmer font-heading text-[14px] font-bold">+{Object.values(certsData).reduce((sum, p) => sum + p.total, 0)}</span> certificaciones cloud activas en la tribu.
              </p>
            </div>

            <div key={tab} className="relative grid sm:grid-cols-2 gap-3">
              {currentProvider?.list.map(({ name, count }, idx) => (
                <div
                  key={name}
                  data-cursor="grow"
                  className="group relative p-5 bg-etribe-gray-bg rounded-[10px] flex items-center justify-between gap-3 opacity-0 animate-fade-in-up cursor-default border border-transparent hover:border-etribe-blue/15 hover:bg-white hover:shadow-[0_8px_24px_-12px_rgba(16,3,163,.25)] hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
                  style={{ animationDelay: `${idx * 40}ms` }}
                >
                  {/* Glow amarillo que aparece en hover */}
                  <span
                    aria-hidden
                    className="absolute -right-8 -top-8 w-24 h-24 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ background: 'radial-gradient(circle, rgba(255,200,54,.35), transparent 70%)' }}
                  />
                  <span className="relative text-[13px] font-medium text-etribe-ink group-hover:text-etribe-blue transition-colors">{name}</span>
                  <span className="relative font-heading text-[22px] text-etribe-blue bg-white w-10 h-10 grid place-items-center rounded-[8px] flex-shrink-0 border border-etribe-line/60 group-hover:bg-etribe-blue group-hover:text-etribe-yellow group-hover:border-etribe-blue group-hover:rotate-[-4deg] transition-all duration-300">
                    {count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* Programa interno */}
        <Reveal>
          <div className="on-blue mt-8 bg-etribe-blue text-white rounded-[20px] p-10 lg:p-16 relative overflow-hidden">
            {/* Grid pattern decorativo */}
            <div
              aria-hidden
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                backgroundImage:
                  'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
                maskImage:
                  'radial-gradient(ellipse at center, black 30%, transparent 75%)',
                WebkitMaskImage:
                  'radial-gradient(ellipse at center, black 30%, transparent 75%)',
              }}
            />
            {/* Glow amarillo top-right */}
            <div
              aria-hidden
              className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(255,200,54,.28), transparent 65%)' }}
            />
            {/* Glow azul bottom-left */}
            <div
              aria-hidden
              className="absolute -bottom-32 -left-32 w-[360px] h-[360px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(50,127,239,.18), transparent 65%)' }}
            />

            <div className="relative">
              {/* Eyebrow con badge */}
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-etribe-yellow/15 border border-etribe-yellow/30 text-etribe-yellow font-heading text-[11px] tracking-[0.18em] uppercase font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-etribe-yellow animate-pulse" />
                  Programa interno
                </span>
                <span className="text-white/50 text-[12px] font-medium hidden sm:inline">Vigente · 2026</span>
              </div>

              <h3 className="font-heading text-[clamp(28px,3vw,40px)] text-white uppercase tracking-[0.015em] mb-5 leading-[1.05] max-w-[20ch]">
                Plan semestral de{' '}
                <span className="text-etribe-yellow">certificación continua</span>
              </h3>

              <p className="text-[clamp(17px,1.3vw,20px)] text-white/80 leading-[1.55] max-w-[60ch] mb-14 text-justify hyphens-auto">
                La excelencia técnica requiere evolución constante. Cada semestre, los profesionales
                de eTribe se integran a programas de certificación especializados alineados con su
                plan de carrera profesional. Este compromiso nos permite impulsar el desarrollo de
                nuestro talento y ofrecer soluciones basadas estrictamente en las últimas
                innovaciones tecnológicas, asegurando nuestra ventaja competitiva en un entorno de
                cambio constante.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-[14px] overflow-hidden p-px">
                {TRAINING_STATS.map((s, idx) => (
                  <Reveal key={s.label} delay={idx * 80}>
                    <div
                      className="relative bg-etribe-blue h-full p-7 lg:p-8 group hover:bg-white/[0.04] transition-colors duration-300"
                      data-cursor="grow"
                    >
                      {/* Indicador de orden vertical */}
                      <span className="absolute top-7 right-7 text-[10px] font-heading tracking-[0.18em] text-white/30">
                        {String(idx + 1).padStart(2, '0')}
                      </span>

                      {/* Icon */}
                      <div className="w-10 h-10 rounded-[10px] bg-etribe-yellow/10 text-etribe-yellow grid place-items-center mb-6 group-hover:bg-etribe-yellow group-hover:text-etribe-blue transition-all duration-400">
                        {s.icon}
                      </div>

                      {/* Big number */}
                      <div className="font-heading font-black text-[clamp(48px,5vw,72px)] text-etribe-yellow leading-none mb-4 tracking-[-0.01em] group-hover:translate-x-1 transition-transform duration-300">
                        {s.num}
                      </div>

                      {/* Label */}
                      <div className="font-heading text-sm tracking-[0.12em] uppercase text-white mb-2.5">
                        {s.label}
                      </div>

                      {/* Yellow divider */}
                      <span
                        aria-hidden
                        className="block h-px w-6 bg-etribe-yellow/40 mb-3 group-hover:w-12 group-hover:bg-etribe-yellow transition-all duration-400"
                      />

                      <p className="text-[13px] text-white/65 leading-[1.5]">{s.sub}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
